import React from 'react';

import { Money, formatMoney } from '@betterment/core';
import { Box, Text } from '@betterment/samba-react';
import { formatNumberWithDecimals } from '@betterment/shared-components';

export interface AllTimeGainsTimeWeightedReturnsType {
  timeWeightedReturns: number | null;
  allTimeGains: Money | null;
}

const CopyContainer: React.FC<React.PropsWithChildren<{ isPositive: boolean }>> = ({ isPositive, children }) => {
  return <Text css={{ color: isPositive ? 'text.success' : 'text.weak' }}>{children}</Text>;
};

const ReturnPercentageCopy: React.FC<{
  timeWeightedReturns: number;
}> = ({ timeWeightedReturns }) => {
  return (
    <CopyContainer isPositive={timeWeightedReturns > 0}>
      {`${timeWeightedReturns >= 1 ? '+' : ''}${formatNumberWithDecimals(timeWeightedReturns)}%`}
    </CopyContainer>
  );
};

const TimeWeightedReturns: React.FC<
  Pick<AllTimeGainsTimeWeightedReturnsType, 'timeWeightedReturns'> & {
    isManagedAccount: boolean;
  }
> = ({ timeWeightedReturns, isManagedAccount }) => {
  if (!isManagedAccount) return null;

  const shouldShowFormattedTimeWeightedReturn = timeWeightedReturns !== null;

  return (
    <Box css={{ display: 'flex' }}>
      {shouldShowFormattedTimeWeightedReturn && <ReturnPercentageCopy timeWeightedReturns={timeWeightedReturns} />}
      &nbsp;
      <Text>time-weighted return</Text>
    </Box>
  );
};

export const AllTimeGainsTimeWeightedReturns: React.FC<
  AllTimeGainsTimeWeightedReturnsType & { isManagedAccount: boolean }
> = ({ timeWeightedReturns, allTimeGains, isManagedAccount }) => {
  const allTimeGainsFormatted = allTimeGains && formatMoney(allTimeGains);

  return (
    <Box css={{ display: 'flex', gap: 'space.200' }}>
      <Box css={{ display: 'flex' }}>
        {allTimeGains && <CopyContainer isPositive={allTimeGains?.cents >= 0}>{allTimeGainsFormatted}</CopyContainer>}
        &nbsp;
        <Text>all time</Text>
      </Box>
      <TimeWeightedReturns isManagedAccount={isManagedAccount} timeWeightedReturns={timeWeightedReturns} />
    </Box>
  );
};
